/**
 * Conflicts, blackouts and manual slots — the calendar collisions a human has to choose between.
 *
 * **The queue is computed on every read, and there is no "resolve" button.** A conflict is a slot
 * somebody has taken on a date its pandit can no longer work; it disappears when the thing causing
 * it changes — the pandit reopens the time, ops lifts the blackout, or the booking moves. A button
 * that marked one "handled" would leave the collision in place and hide it, which is the one
 * outcome worse than a long queue.
 *
 * **Closing a date never cancels a booking.** The confirm says so before ops clicks, and the
 * response lists exactly which bookings the closure collides with. Two true facts — the pandit
 * cannot come, somebody has paid — need a person, and this screen is where that person stands.
 *
 * **A manual slot is an exception, not a tool.** It opens one start the pandit's own patterns do
 * not express, for a phone arrangement, and the generator never takes it away again.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useBlackouts,
  useConflicts,
  useCreateBlackout,
  useCreateManualSlot,
  useDeleteBlackout,
} from '../lib/api/hooks/availability';
import { formatDateTime } from '../lib/datetime';
import { istToday } from './Flags';

/** `platform` closes everything; the other two need the thing they close. */
const SCOPES = ['platform', 'city', 'pandit'];

/** ISO dates, because that is what the API takes and what the picker gives. */
export function isoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

export function windowFromToday(days: number): { from: string; to: string } {
  const now = new Date();
  const later = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  return { from: isoDate(now), to: isoDate(later) };
}

/**
 * The two reasons a slot can be in conflict read differently to an operator.
 *
 * A blackout is ours to lift; a pandit closing their own time is theirs, and the next action is a
 * phone call rather than a click.
 */
/**
 * How far ahead the blackout list reads, and so how far ahead one may be closed: a date past the
 * list's end was created, then invisible, then impossible to lift from here.
 */
export const BLACKOUT_DAYS = 366;

/**
 * A blackout date is a real calendar day from today (IST) to `last` — closing a past day does
 * nothing, and one past the list's window could never be lifted from this screen.
 */
export function blackoutDateError(value: string, today: string, last = '9999-12-31'): string | undefined {
  if (value === '') return undefined;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
  ) {
    return 'A date as YYYY-MM-DD';
  }
  if (value < today) return 'Today or later';
  return value <= last ? undefined : `${last} at the latest — the list below reads no further`;
}

const ZONED = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/;

/**
 * A manual slot's start must carry its zone — the server would read a bare time as UTC, five and
 * a half hours from what an operator on the phone in India meant — and be still to come.
 */
export function manualStartError(value: string, now: number): string | undefined {
  if (value === '') return undefined;
  const at = Date.parse(value);
  if (!ZONED.test(value) || Number.isNaN(at)) return 'A time with its zone, e.g. 2026-11-02T09:00+05:30';
  return at > now ? undefined : 'A start still to come';
}

export function conflictTone(reason: string): 'error' | 'warn' {
  return reason.includes('blacked out') ? 'error' : 'warn';
}

export function Conflicts() {
  const { scope, toast } = useConsole();
  const [page, setPage] = useState(1);
  const [scopeKind, setScopeKind] = useState('platform');
  const [blackoutOn, setBlackoutOn] = useState('');
  const [reason, setReason] = useState('');
  const [targetId, setTargetId] = useState('');
  const [manualPandit, setManualPandit] = useState('');
  const [manualStart, setManualStart] = useState('');
  const [confirm, setConfirm] = useState<'blackout' | number | null>(null);

  const window = windowFromToday(BLACKOUT_DAYS);
  const dateError = blackoutDateError(blackoutOn, istToday(), window.to);
  const startError = manualStartError(manualStart, Date.now());
  const conflicts = useConflicts({ page, page_size: 50 });
  const blackouts = useBlackouts(window);
  const createBlackout = useCreateBlackout();
  const deleteBlackout = useDeleteBlackout();
  const createSlot = useCreateManualSlot();

  const fail = (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) });
  const rows = conflicts.data?.items ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-lg)', alignItems: 'start' }}>
      <Section
        title="Conflicts"
        note="Taken slots the calendar now contradicts — resolved by changing the cause, not by ticking them off"
        pad={false}
      >
        {conflicts.isPending && <LoadingState message="Reading the conflicts queue…" />}
        {conflicts.isError && (
          <ErrorState
            title="Could not read the conflicts queue"
            cause={errorMessage(conflicts.error)}
          />
        )}
        {conflicts.isSuccess && rows.length === 0 && (
          <EmptyState
            icon="calendar-x"
            title="Nothing is in conflict"
            body="Every taken slot is on a date its pandit can still work."
            inline
          />
        )}
        {rows.length > 0 && (
          <>
            <DataTable
              density="compact"
              columns={[
                { key: 'when', label: 'Starts', mono: true },
                { key: 'pandit', label: 'Pandit', mono: true },
                { key: 'state', label: 'Slot' },
                { key: 'why', label: 'Why' },
              ]}
              rows={rows.map((c) => ({
                id: c.slot.id,
                when: formatDateTime(c.slot.start_at),
                pandit: `#${c.pandit_profile_id}`,
                state: <StatusChip tone="info" label={c.slot.status} />,
                why: <StatusChip tone={conflictTone(c.reason)} label={c.reason} />,
              }))}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-md)',
                padding: 'var(--sp-md) var(--sp-lg)',
                fontSize: 'var(--fs-admin-colhead)',
                color: 'var(--c-ink3)',
              }}
            >
              <span>
                {rows.length} of {conflicts.data!.total}
                {conflicts.data!.truncated ? '+ (the scan was capped)' : ''}
              </span>
              <span style={{ flex: 1 }} />
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="ghost"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="ghost"
                disabled={page * 50 >= conflicts.data!.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </ScopeAction>
            </div>
          </>
        )}
      </Section>

      <Section title="Blackouts" note="Closed dates. Open slots are blocked; booked ones become conflicts" pad={false}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-md)',
            alignItems: 'end',
            flexWrap: 'wrap',
            padding: 'var(--sp-lg)',
          }}
        >
          <Select label="Scope" value={scopeKind} options={SCOPES} onChange={setScopeKind} />
          {scopeKind !== 'platform' && (
            <Input
              /* Named for what it closes, not just for what it is — the manual-slot
                 form below also asks for a pandit id, and two fields labelled the
                 same on one screen is how the wrong one gets filled in. */
              label={scopeKind === 'city' ? 'City to close' : 'Pandit to close'}
              value={targetId}
              onChange={(v) => setTargetId(v.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              style={{ maxWidth: 140 }}
            />
          )}
          <Input
            label="Date"
            value={blackoutOn}
            onChange={setBlackoutOn}
            placeholder="2026-09-15"
            error={dateError}
          />
          <Input
            label="Reason"
            value={reason}
            onChange={setReason}
            style={{ minWidth: 260 }}
          />
          <ScopeAction
            scope={scope}
            need="ops_dispatch"
            size="sm"
            icon="calendar-x"
            disabled={
              blackoutOn.length === 0 ||
              dateError !== undefined ||
              reason.trim().length === 0 ||
              (scopeKind !== 'platform' && targetId === '')
            }
            onClick={() => setConfirm('blackout')}
          >
            Close this date
          </ScopeAction>
        </div>

        {blackouts.isPending && <LoadingState message="Reading blackouts…" />}
        {blackouts.isError && (
          <ErrorState title="Could not read blackouts" cause={errorMessage(blackouts.error)} />
        )}
        {blackouts.isSuccess && blackouts.data.items.length === 0 && (
          <EmptyState icon="calendar-x" title="No date is closed in the next year" inline />
        )}
        {blackouts.data && blackouts.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'on', label: 'Date', mono: true },
              { key: 'scope', label: 'Scope' },
              { key: 'target', label: 'Applies to', mono: true },
              { key: 'why', label: 'Reason' },
              { key: 'act', label: '' },
            ]}
            rows={blackouts.data.items.map((b) => ({
              id: b.id,
              on: b.blackout_on,
              scope: <StatusChip tone={b.scope === 'platform' ? 'error' : 'warn'} label={b.scope} />,
              target:
                b.city_id !== null && b.city_id !== undefined
                  ? `city #${b.city_id}`
                  : b.pandit_profile_id !== null && b.pandit_profile_id !== undefined
                    ? `pandit #${b.pandit_profile_id}`
                    : 'everyone',
              why: b.reason,
              act: (
                <ScopeAction
                  scope={scope}
                  need="ops_dispatch"
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirm(b.id)}
                >
                  Lift
                </ScopeAction>
              ),
            }))}
          />
        )}
      </Section>

      <Section
        title="Manual slot"
        note="One start the pandit's own patterns do not express — for a phone arrangement"
      >
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
          <Input
            label="Pandit id"
            value={manualPandit}
            onChange={(v) => setManualPandit(v.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            style={{ maxWidth: 140 }}
          />
          <Input
            label="Starts at"
            value={manualStart}
            onChange={setManualStart}
            placeholder="2026-09-15T09:00+05:30"
            error={startError}
            helper={
              manualStart && !startError ? `Opens ${formatDateTime(manualStart)}` : undefined
            }
            style={{ minWidth: 260 }}
          />
          <ScopeAction
            scope={scope}
            need="ops_dispatch"
            size="sm"
            icon="plus"
            disabled={
              manualPandit.length === 0 || manualStart.length === 0 || startError !== undefined
            }
            onClick={() =>
              createSlot.mutate(
                { profileId: Number(manualPandit), startAt: manualStart },
                {
                  onSuccess: () => {
                    setManualStart('');
                    toast({
                      tone: 'success',
                      message: 'Slot opened · the generator will leave it alone',
                    });
                  },
                  onError: fail,
                },
              )
            }
          >
            Open it
          </ScopeAction>
        </div>
      </Section>

      <ConfirmAction
        open={confirm === 'blackout'}
        title={`Close ${blackoutOn} for ${scopeKind === 'platform' ? 'everyone' : `this ${scopeKind}`}?`}
        phrase="CLOSE"
        tone="error"
        consequences={[
          'Every open slot on that date is blocked immediately.',
          'Bookings already taken are NOT cancelled — they come back as conflicts for somebody to work through.',
          scopeKind === 'platform'
            ? 'Platform scope closes the date in every city, for every pandit.'
            : `Only the ${scopeKind} you named is affected.`,
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          createBlackout.mutate(
            {
              scope: scopeKind,
              blackout_on: blackoutOn,
              reason: reason.trim(),
              city_id: scopeKind === 'city' ? Number(targetId) : null,
              pandit_profile_id: scopeKind === 'pandit' ? Number(targetId) : null,
            },
            {
              onSuccess: (created) => {
                setReason('');
                setBlackoutOn('');
                toast({
                  tone: created.conflicts.length > 0 ? 'info' : 'success',
                  message:
                    created.conflicts.length > 0
                      ? `Date closed · ${created.conflicts.length} booking${created.conflicts.length === 1 ? '' : 's'} now in conflict`
                      : 'Date closed · nothing was booked',
                });
              },
              onError: fail,
            },
          );
        }}
        confirmLabel="Close the date"
      />

      <ConfirmAction
        open={typeof confirm === 'number'}
        title="Lift this blackout?"
        phrase="LIFT"
        tone="warn"
        consequences={[
          'Slots come back on the next generation run, not immediately.',
          'That is deliberate: only the generator knows whether a pattern still covers those minutes, and reopening them here would undo a pandit who had separately closed the day.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const id = confirm as number;
          setConfirm(null);
          deleteBlackout.mutate(id, {
            onSuccess: () => toast({ tone: 'info', message: 'Blackout lifted' }),
            onError: fail,
          });
        }}
        confirmLabel="Lift it"
      />
    </div>
  );
}
